/**
 * Configurações > Calendário: os dias sem expediente da unidade (feriados
 * nacionais e locais, recessos, pontos facultativos).
 *
 * É o calendário ÚNICO da unidade: a Produção, as Solicitações e a
 * Capacidade da Fábrica contam os dias úteis por ele (segunda a sexta menos o
 * que está aqui). Antes eram duas listas, e a do Legado nem tinha o 20/11.
 *
 * Todo mundo logado vê; só quem tem `calendario:editar` (o PCP) muda. Quem
 * decide é o backend (`pode_editar` na resposta).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";

import AsyncButton from "../../../components/AsyncButton";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import { invalidarCalendarioUnidade } from "../../../hooks/useCalendarioUnidade";
import {
  TIPOS_DIA_NAO_UTIL,
  atualizarDiaNaoUtil,
  criarDiaNaoUtil,
  criarIntervalo,
  listarDiasNaoUteis,
  removerDiaNaoUtil,
  semearNacionais,
} from "../../../services/calendario";
import { diaDaSemana, hojeIso } from "../../../utils/capacidade/calendario";
import { formatarDataLocal, plural } from "../../../utils/dinheiro";

const TAMANHO_DESCRICAO = 120;
const DIAS_DA_SEMANA = ["domingo", "segunda", "terça", "quarta", "quinta", "sexta", "sábado"];

const estiloTexto = { fontSize: 13, color: "var(--ink-2)", lineHeight: 1.5 };
const estiloTextoFraco = { fontSize: 12, color: "var(--ink-3)", lineHeight: 1.5 };
const estiloLinhaForm = { display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 10, minWidth: 0 };
const estiloCampo = { display: "flex", flexDirection: "column", gap: 4, minWidth: 0 };
const estiloLista = {
  border: "1px solid var(--border)",
  borderRadius: 10,
  background: "var(--surface)",
  overflow: "hidden",
  minWidth: 0,
};
/* Celular: data e ações em cima, descrição embaixo. Desktop: uma linha só.
   Sem rolagem lateral em nenhuma largura (colunas com mínimo zero). */
const LinhaDiaBox = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-areas:
    "data acoes"
    "descricao descricao";
  column-gap: 12px;
  row-gap: 2px;
  align-items: center;
  padding: 9px 12px;
  font-size: 13px;
  min-width: 0;

  .data {
    grid-area: data;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
  .data small {
    margin-left: 6px;
    font-weight: 400;
    font-size: 12px;
    color: var(--ink-3);
  }
  .descricao {
    grid-area: descricao;
    color: var(--ink-2);
  }
  .descricao small {
    font-size: 12px;
    color: var(--ink-3);
  }
  .acoes {
    grid-area: acoes;
    display: flex;
    gap: 6px;
    justify-content: flex-end;
    flex-wrap: wrap;
  }
  .data,
  .descricao {
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  @media (min-width: 769px) {
    grid-template-columns: minmax(0, 14em) minmax(0, 1fr) auto;
    grid-template-areas: "data descricao acoes";
  }
`;


const rotuloDoTipo = (valor) => TIPOS_DIA_NAO_UTIL.find((t) => t.valor === valor)?.rotulo || valor;

function SeletorTipo({ id, valor, onChange }) {
  return (
    <select id={id} className="select" value={valor} onChange={(evento) => onChange(evento.target.value)}>
      {TIPOS_DIA_NAO_UTIL.map((tipo) => (
        <option key={tipo.valor} value={tipo.valor}>
          {tipo.rotulo}
        </option>
      ))}
    </select>
  );
}

function Campo({ rotulo, htmlFor, children, largura }) {
  return (
    <div style={{ ...estiloCampo, flex: largura ? `1 1 ${largura}` : "0 0 auto" }}>
      <label className="input-label" htmlFor={htmlFor}>
        {rotulo}
      </label>
      {children}
    </div>
  );
}

/** Uma linha do ano: ver, editar tipo/descrição e remover (dois toques). */
function LinhaDia({ dia, podeEditar, onAtualizar, onRemover }) {
  const [modo, setModo] = useState("ver");
  const [tipo, setTipo] = useState(dia.tipo);
  const [descricao, setDescricao] = useState(dia.descricao);

  const salvar = async () => {
    await onAtualizar(dia, { tipo, descricao: descricao.trim() });
    setModo("ver");
  };

  // Cancelar descarta o rascunho: o próximo "Editar" abre com o valor gravado.
  const cancelarEdicao = () => {
    setTipo(dia.tipo);
    setDescricao(dia.descricao);
    setModo("ver");
  };

  if (modo === "editar") {
    return (
      <div style={{ ...estiloLinhaForm, padding: "10px 12px" }} data-dia={dia.data}>
        <strong style={{ fontSize: 13, alignSelf: "center" }}>{formatarDataLocal(dia.data)}</strong>
        <Campo rotulo="Tipo" htmlFor={`tipo-${dia.id}`}>
          <SeletorTipo id={`tipo-${dia.id}`} valor={tipo} onChange={setTipo} />
        </Campo>
        <Campo rotulo="Descrição" htmlFor={`descricao-${dia.id}`} largura="14em">
          <input
            id={`descricao-${dia.id}`}
            className="input"
            maxLength={TAMANHO_DESCRICAO}
            value={descricao}
            onChange={(evento) => setDescricao(evento.target.value)}
          />
        </Campo>
        <AsyncButton variant="primary" disabled={!descricao.trim()} successMessage="Dia atualizado." onClick={salvar}>
          Salvar
        </AsyncButton>
        <button type="button" className="btn btn--ghost btn--sm" onClick={cancelarEdicao}>
          Cancelar
        </button>
      </div>
    );
  }

  const rotuloTipo = dia.tipo_label || rotuloDoTipo(dia.tipo);
  return (
    <LinhaDiaBox data-dia={dia.data}>
      <span className="data">
        {formatarDataLocal(dia.data)}
        <small>{DIAS_DA_SEMANA[diaDaSemana(dia.data)]}</small>
      </span>
      <span className="descricao" title={`${dia.descricao} · ${rotuloTipo}`}>
        {dia.descricao} <small>· {rotuloTipo}</small>
      </span>
      {podeEditar ? (
        <span className="acoes">
          {modo === "confirmar" ? (
            <>
              <AsyncButton variant="danger" successMessage="Dia removido." onClick={() => onRemover(dia)}>
                Confirmar remoção
              </AsyncButton>
              <button type="button" className="btn btn--ghost btn--sm" onClick={() => setModo("ver")}>
                Cancelar
              </button>
            </>
          ) : (
            <>
              <button type="button" className="btn btn--ghost btn--sm" onClick={() => setModo("editar")}>
                Editar
              </button>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                style={{ color: "var(--danger-text)" }}
                onClick={() => setModo("confirmar")}
              >
                Remover
              </button>
            </>
          )}
        </span>
      ) : (
        <span className="acoes" />
      )}
    </LinhaDiaBox>
  );
}

function NovoDia({ ano, onCriar }) {
  const [data, setData] = useState("");
  const [tipo, setTipo] = useState("feriado_local");
  const [descricao, setDescricao] = useState("");

  const criar = async () => {
    await onCriar({ data, tipo, descricao: descricao.trim() });
    setData("");
    setDescricao("");
  };

  return (
    <div className="set-row">
      <div>
        <div className="set-row__lbl">Adicionar dia</div>
        <div className="set-row__desc">Feriado municipal, ponto facultativo ou um dia avulso sem expediente.</div>
      </div>
      <div className="set-row__control">
        <div style={estiloLinhaForm}>
          <Campo rotulo="Data" htmlFor="novo-dia-data">
            <input
              id="novo-dia-data"
              type="date"
              className="input"
              min={`${ano}-01-01`}
              max={`${ano}-12-31`}
              value={data}
              onChange={(evento) => setData(evento.target.value)}
            />
          </Campo>
          <Campo rotulo="Tipo" htmlFor="novo-dia-tipo">
            <SeletorTipo id="novo-dia-tipo" valor={tipo} onChange={setTipo} />
          </Campo>
          <Campo rotulo="Descrição" htmlFor="novo-dia-descricao" largura="14em">
            <input
              id="novo-dia-descricao"
              className="input"
              maxLength={TAMANHO_DESCRICAO}
              placeholder="Ex.: Aniversário da cidade"
              value={descricao}
              onChange={(evento) => setDescricao(evento.target.value)}
            />
          </Campo>
          <AsyncButton disabled={!data || !descricao.trim()} successMessage="Dia adicionado." onClick={criar}>
            Adicionar dia
          </AsyncButton>
        </div>
      </div>
    </div>
  );
}

function NovoIntervalo({ ano, onCriar }) {
  const toast = useToast();
  const [de, setDe] = useState("");
  const [ate, setAte] = useState("");
  const [tipo, setTipo] = useState("recesso");
  const [descricao, setDescricao] = useState("");

  const criar = async () => {
    const resposta = await onCriar({ de, ate, tipo, descricao: descricao.trim() });
    const criados = resposta?.criados?.length || 0;
    const ignorados = resposta?.ignorados?.length || 0;
    toast.success(
      `${plural(criados, "dia adicionado", "dias adicionados")}` +
        (ignorados ? ` · ${plural(ignorados, "já estava cadastrado", "já estavam cadastrados")}` : "") +
        ".",
    );
    setDe("");
    setAte("");
    setDescricao("");
  };

  return (
    <div className="set-row">
      <div>
        <div className="set-row__lbl">Adicionar intervalo</div>
        <div className="set-row__desc">
          Recesso de fim de ano, férias coletivas. Entram só os dias de segunda a sexta; até 62 dias por vez.
        </div>
      </div>
      <div className="set-row__control">
        <div style={estiloLinhaForm}>
          <Campo rotulo="De" htmlFor="intervalo-de">
            <input
              id="intervalo-de"
              type="date"
              className="input"
              min={`${ano - 1}-01-01`}
              value={de}
              onChange={(evento) => setDe(evento.target.value)}
            />
          </Campo>
          <Campo rotulo="Até" htmlFor="intervalo-ate">
            <input
              id="intervalo-ate"
              type="date"
              className="input"
              min={de || undefined}
              value={ate}
              onChange={(evento) => setAte(evento.target.value)}
            />
          </Campo>
          <Campo rotulo="Tipo" htmlFor="intervalo-tipo">
            <SeletorTipo id="intervalo-tipo" valor={tipo} onChange={setTipo} />
          </Campo>
          <Campo rotulo="Descrição" htmlFor="intervalo-descricao" largura="12em">
            <input
              id="intervalo-descricao"
              className="input"
              maxLength={TAMANHO_DESCRICAO}
              placeholder="Ex.: Recesso de fim de ano"
              value={descricao}
              onChange={(evento) => setDescricao(evento.target.value)}
            />
          </Campo>
          <AsyncButton disabled={!de || !ate || ate < de || !descricao.trim()} silentSuccess onClick={criar}>
            Adicionar intervalo
          </AsyncButton>
        </div>
      </div>
    </div>
  );
}

const ESTADO_INICIAL = Object.freeze({ ano: null, dias: [], anos: [], podeEditar: false, carregado: false, erro: null });

export default function CalendarioTab() {
  const { request } = useAuth();
  const toast = useToast();
  const [ano, setAno] = useState(() => Number(hojeIso().slice(0, 4)));
  const [estado, setEstado] = useState(ESTADO_INICIAL);
  // Último ano pedido: trocando de ano depressa, a resposta de um ano que já
  // saiu da tela chega depois e não pode sobrescrever o ano atual.
  const anoPedido = useRef(null);

  const carregar = useCallback(async () => {
    anoPedido.current = ano;
    try {
      const resposta = await listarDiasNaoUteis(request, { de: `${ano}-01-01`, ate: `${ano}-12-31` });
      if (anoPedido.current !== ano) return;
      setEstado({
        ano,
        dias: Array.isArray(resposta?.dias) ? resposta.dias : [],
        anos: (resposta?.anos_com_registro || []).map(Number),
        podeEditar: Boolean(resposta?.pode_editar),
        carregado: true,
        erro: null,
      });
    } catch (erro) {
      // Erro de um ano que já saiu da tela não é mostrado: o ano atual tem a própria leitura.
      if (anoPedido.current !== ano) return;
      setEstado({ ...ESTADO_INICIAL, ano, carregado: true, erro: erro?.message || "Não foi possível ler o calendário." });
    }
  }, [request, ano]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // Toda escrita relê o ano e avisa o cache das telas que contam dias úteis.
  const depoisDeMudar = useCallback(async () => {
    invalidarCalendarioUnidade();
    await carregar();
  }, [carregar]);

  const criarDia = useCallback(
    async (corpo) => {
      await criarDiaNaoUtil(request, corpo);
      await depoisDeMudar();
    },
    [request, depoisDeMudar],
  );
  const criarFaixa = useCallback(
    async (corpo) => {
      const resposta = await criarIntervalo(request, corpo);
      await depoisDeMudar();
      return resposta;
    },
    [request, depoisDeMudar],
  );
  const atualizar = useCallback(
    async (dia, corpo) => {
      await atualizarDiaNaoUtil(request, dia.id, corpo);
      await depoisDeMudar();
    },
    [request, depoisDeMudar],
  );
  const remover = useCallback(
    async (dia) => {
      await removerDiaNaoUtil(request, dia.id);
      await depoisDeMudar();
    },
    [request, depoisDeMudar],
  );
  const semear = useCallback(async () => {
    const resposta = await semearNacionais(request, ano);
    await depoisDeMudar();
    toast.success(`${plural(resposta?.criados?.length || 0, "feriado nacional adicionado", "feriados nacionais adicionados")}.`);
  }, [request, ano, depoisDeMudar, toast]);

  const atual = estado.ano === ano && estado.carregado;
  const anoSemRegistro = atual && !estado.erro && !estado.anos.includes(ano);

  return (
    <section className="set-section">
      <div className="set-section__head">
        <h2 className="set-section__title">Calendário</h2>
        <p className="set-section__desc">
          Feriados, recessos e pontos facultativos da unidade. A Produção, as Solicitações e a Capacidade da Fábrica
          contam os dias úteis por aqui: segunda a sexta, menos os dias desta lista.
        </p>
      </div>
      <div className="set-section__body">
        <div className="set-row">
          <div>
            <div className="set-row__lbl">Ano</div>
            <div className="set-row__desc">Ano sem nenhum dia cadastrado bloqueia a Capacidade da Fábrica daquele ano.</div>
          </div>
          <div
            className="set-row__control"
            style={{ display: "flex", flexDirection: "row", flexWrap: "nowrap", alignItems: "center", gap: 8 }}
          >
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              aria-label="Ano anterior"
              onClick={() => setAno((a) => a - 1)}
            >
              ‹
            </button>
            <strong style={{ fontSize: 15, minWidth: 48, textAlign: "center", fontVariantNumeric: "tabular-nums" }}>
              {ano}
            </strong>
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              aria-label="Próximo ano"
              onClick={() => setAno((a) => a + 1)}
            >
              ›
            </button>
          </div>
        </div>

        {!atual ? <div style={estiloTextoFraco}>Carregando…</div> : null}
        {atual && estado.erro ? (
          <div
            style={{
              padding: "8px 12px",
              borderRadius: 8,
              fontSize: 13,
              background: "var(--danger-bg)",
              color: "var(--danger-text)",
              border: "1px solid var(--danger-border)",
            }}
          >
            {estado.erro}
          </div>
        ) : null}

        {atual && !estado.erro ? (
          <>
            {estado.dias.length === 0 ? (
              <div style={estiloTexto}>Nenhum dia sem expediente cadastrado em {ano}.</div>
            ) : (
              <div style={estiloLista} aria-label={`Dias sem expediente em ${ano}`}>
                {estado.dias.map((dia, i) => (
                  <div key={dia.id} style={i > 0 ? { borderTop: "1px solid var(--border)" } : undefined}>
                    <LinhaDia dia={dia} podeEditar={estado.podeEditar} onAtualizar={atualizar} onRemover={remover} />
                  </div>
                ))}
              </div>
            )}
            {anoSemRegistro && estado.podeEditar ? (
              <div>
                <AsyncButton variant="secondary" silentSuccess onClick={semear}>
                  Preencher feriados nacionais de {ano}
                </AsyncButton>
              </div>
            ) : null}
            {estado.podeEditar ? (
              <>
                <NovoDia ano={ano} onCriar={criarDia} />
                <NovoIntervalo ano={ano} onCriar={criarFaixa} />
              </>
            ) : (
              <div style={estiloTextoFraco}>
                Só quem tem a permissão de editar o calendário (normalmente o PCP) pode mudar esta lista.
              </div>
            )}
          </>
        ) : null}
      </div>
    </section>
  );
}
